import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  InputAdornment,
  Alert,
  Divider,
  Card,
  CardContent,
  Stack,
  Tooltip,
  Autocomplete,
} from '@mui/material'
import {
  Visibility,
  CheckCircle,
  Search as SearchIcon,
  Sync as SyncIcon,
  Description,
  CalendarToday,
  Assignment,
  Verified,
  Edit,
  Person as PersonIcon,
  Download as DownloadIcon,
} from '@mui/icons-material'
import SignatureCanvas from 'react-signature-canvas'
import { toast } from 'react-toastify'
import api from '@/services/api'
import { useAuth } from '@/hooks/useAuth'
import AdvancedPagination from '@/components/pagination/AdvancedPagination'
import { useDebounce } from '@/hooks/useDebounce'
import { markdownToHtml } from '@/utils/markdown'
import {
  getContractDropdowns,
  ContractDropdownOption,
} from '@/services/invoicesService'

// Helper function to capitalize first letter
const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

// Helper function to get contract ID from nested structure
const getContractId = (contractId: PoCDocument['contract_id']): string => {
  if (!contractId) return 'N/A'
  if (typeof contractId === 'string') return contractId
  return contractId.contract_ids || 'N/A'
}

// Helper function to get session ID from nested structure
const getSessionId = (sessionId: PoCDocument['session_id']): string => {
  if (!sessionId) return 'N/A'
  if (typeof sessionId === 'string') return sessionId
  return sessionId.session_ids || 'N/A'
}

interface PoCDocument {
  _id: string
  contract_id?:
    | {
        contract_ids?: string
      }
    | string
  session_id?:
    | {
        session_ids?: string
      }
    | string
  poc_doc: string
  poc_name: string
  poc_status: 'draft' | 'pending' | 'submitted' | 'certified' | 'rejected'
  authorization_status: 'pending' | 'approved' | 'denied'
  is_deleted: boolean
  createdAt: string
  updatedAt: string
  pdf_url?: string
}

interface PoCResponse {
  message: string
  data: PoCDocument[]
  pagination: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

const PoCDocumentsPage: React.FC = () => {
  const { token, user } = useAuth()
  const [documents, setDocuments] = useState<PoCDocument[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedDocument, setSelectedDocument] = useState<PoCDocument | null>(
    null
  )
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [showSignatureSection, setShowSignatureSection] = useState(false)
  const [certifying, setCertifying] = useState(false)
  const [signatureData, setSignatureData] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [contractId, setContractId] = useState<string>('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [total, setTotal] = useState(0)
  const signaturePadRef = useRef<SignatureCanvas>(null)

  // Dropdown options
  const [contractOptions, setContractOptions] = useState<
    ContractDropdownOption[]
  >([])
  const [loadingDropdowns, setLoadingDropdowns] = useState(false)
  const [selectedContract, setSelectedContract] =
    useState<ContractDropdownOption | null>(null)

  const debouncedSearch = useDebounce(search, 500)

  // Fetch dropdown options
  useEffect(() => {
    const fetchDropdowns = async () => {
      setLoadingDropdowns(true)
      try {
        const contracts = await getContractDropdowns()
        setContractOptions(contracts)
      } catch (err) {
        console.error('Error fetching contract dropdowns:', err)
      } finally {
        setLoadingDropdowns(false)
      }
    }
    fetchDropdowns()
  }, [])

  // Fetch PoC documents
  const fetchDocuments = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params: Record<string, any> = {
        page: page + 1,
        limit: rowsPerPage,
      }

      // Add search query param if provided
      if (debouncedSearch) {
        params.search = debouncedSearch
      }

      // Add contract_id query param if provided
      if (contractId) {
        params.contract_id = contractId
      }

      const response = await api.get<PoCResponse>('sessions/get-all-poc', {
        params,
      })
      setDocuments(response.data.data || [])
      setTotal(response.data.pagination?.total || 0)
    } catch (err: any) {
      console.error('Failed to fetch PoC documents:', err)
      setError(err.response?.data?.message || 'Failed to fetch PoC documents')
      toast.error('Failed to load PoC documents')
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, debouncedSearch, contractId])

  useEffect(() => {
    fetchDocuments()
  }, [fetchDocuments])

  // Certify a PoC document
  const handleCertify = async (pocId: string, signature: string) => {
    setCertifying(true)
    try {
      await api.patch('sessions/certify-poc', {
        poc_id: pocId,
        signature: signature, // Include signature in the request
      })
      toast.success('PoC document certified successfully!')
      setViewDialogOpen(false)
      setShowSignatureSection(false)
      setSelectedDocument(null)
      setSignatureData(null)
      if (signaturePadRef.current) {
        signaturePadRef.current.clear()
      }
      fetchDocuments() // Refresh the list
    } catch (err: any) {
      console.error('Failed to certify PoC document:', err)
      toast.error(
        err.response?.data?.message || 'Failed to certify PoC document'
      )
    } finally {
      setCertifying(false)
    }
  }

  const handleView = (document: PoCDocument) => {
    setSelectedDocument(document)
    setViewDialogOpen(true)
  }

  const handleDownloadPdf = (pdfUrl: string, documentName: string) => {
    if (!pdfUrl) return

    // Create a temporary anchor element to trigger download
    const link = document.createElement('a')
    link.href = pdfUrl
    link.download = documentName || 'PoC_Document.pdf'
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleCertifyClick = (document: PoCDocument) => {
    setShowSignatureSection(true) // Show signature section in the same dialog
  }

  const handleClearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear()
      setSignatureData(null)
    }
  }

  const handleSignatureEnd = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      const dataURL = signaturePadRef.current.toDataURL('image/png')
      setSignatureData(dataURL)
    } else {
      setSignatureData(null)
    }
  }

  const handleCertifyWithSignature = () => {
    if (!signatureData) {
      toast.error('Please provide your signature before certifying')
      return
    }
    if (selectedDocument) {
      handleCertify(selectedDocument._id, signatureData)
    }
  }

  const handleCancelCertify = () => {
    setShowSignatureSection(false)
    setSelectedDocument(null)
    setSignatureData(null)
    if (signaturePadRef.current) {
      signaturePadRef.current.clear()
    }
    setViewDialogOpen(false)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'certified':
        return 'success'
      case 'pending':
        return 'warning'
      case 'draft':
        return 'default'
      case 'submitted':
        return 'info'
      case 'rejected':
        return 'error'
      default:
        return 'default'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const handleClearFilters = () => {
    setSearch('')
    setContractId('')
    setSelectedContract(null)
    setPage(0)
  }

  const hasActiveFilters = Boolean(search || contractId || selectedContract)

  return (
    <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} color="text.primary" mb={1}>
          PoC Documents
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage and certify Plan of Care documents
        </Typography>
      </Box>

      {/* Filters Card */}
      <Card
        sx={{
          mb: 3,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          borderRadius: '1rem',
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <CardContent sx={{ p: 3.5 }}>
          <Box>
            {/* Main Filters Row */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'flex-end',
              }}
            >
              {/* Search Filter */}
              <Box
                sx={{
                  flex: {
                    xs: '1 1 100%',
                    sm: '1 1 calc(50% - 8px)',
                    md: '1 1 calc(40% - 8px)',
                    lg: '2 1 200px',
                  },
                  minWidth: { xs: '100%', sm: '200px' },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1.25,
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.8125rem',
                    letterSpacing: '0.02em',
                  }}
                >
                  Search
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search by Document ID, Contract ID or Session ID..."
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ mr: 0.5 }}>
                        <SearchIcon
                          fontSize="small"
                          sx={{ color: 'text.secondary', fontSize: '18px' }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '0.625rem',
                      backgroundColor: 'grey.50',
                      fontSize: '0.875rem',
                      '&:hover': {
                        backgroundColor: 'grey.100',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'background.paper',
                      },
                      '& fieldset': {
                        borderColor: 'grey.300',
                      },
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: '1.5px',
                      },
                    },
                  }}
                />
              </Box>

              {/* Contract ID Filter */}
              <Box
                sx={{
                  flex: {
                    xs: '1 1 100%',
                    sm: '1 1 calc(50% - 8px)',
                    md: '1 1 calc(30% - 8px)',
                    lg: '1 1 180px',
                  },
                  minWidth: { xs: '100%', sm: '180px' },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1.25,
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.8125rem',
                    letterSpacing: '0.02em',
                  }}
                >
                  Contract Id
                </Typography>
                <Autocomplete
                  options={contractOptions}
                  getOptionLabel={option => option.contract_ids || ''}
                  value={selectedContract}
                  onChange={(event, newValue) => {
                    setSelectedContract(newValue)
                    setContractId(newValue?._id || '')
                  }}
                  loading={loadingDropdowns}
                  size="small"
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder="Select Contract ID"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start" sx={{ mr: 0.5 }}>
                              <PersonIcon
                                fontSize="small"
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: '18px',
                                }}
                              />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '0.625rem',
                          backgroundColor: 'grey.50',
                          fontSize: '0.875rem',
                          '&:hover': {
                            backgroundColor: 'grey.100',
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'background.paper',
                          },
                          '& fieldset': {
                            borderColor: 'grey.300',
                          },
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                            borderWidth: '1.5px',
                          },
                        },
                      }}
                    />
                  )}
                  slotProps={{
                    popper: { sx: { zIndex: 9999 } },
                  }}
                />
              </Box>

              {/* Reset Filter Button */}
              <Box
                sx={{
                  flex: {
                    xs: '1 1 100%',
                    sm: '0 0 auto',
                  },
                  minWidth: { xs: '100%', sm: 'auto' },
                  display: 'flex',
                  alignItems: 'flex-end',
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<SyncIcon sx={{ fontSize: '18px' }} />}
                  onClick={handleClearFilters}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    borderRadius: '0.625rem',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    fontWeight: 500,
                    fontSize: '0.8125rem',
                    px: 2.5,
                    py: 1,
                    height: '40px',
                    minWidth: '100px',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider' }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600 }}>PoC Document</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Contract ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Session ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Authorization</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {hasActiveFilters
                      ? 'No PoC documents found matching your filters. Try adjusting or clearing your filters.'
                      : 'No PoC documents found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              documents.map(doc => (
                <TableRow key={doc._id} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      fontWeight={500}
                    >
                      {doc.poc_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {getContractId(doc.contract_id)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {getSessionId(doc.session_id)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={capitalize(doc.poc_status)}
                      color={getStatusColor(doc.poc_status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={capitalize(doc.authorization_status)}
                      color={
                        doc.authorization_status === 'approved'
                          ? 'success'
                          : doc.authorization_status === 'denied'
                            ? 'error'
                            : 'warning'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(doc.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}
                    >
                      <Tooltip title="View document">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleView(doc)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {doc.pdf_url && (
                        <Tooltip title="Download PoC Document">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() =>
                              handleDownloadPdf(doc.pdf_url!, doc.poc_name)
                            }
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* {doc.poc_status !== 'certified' && (
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleCertifyClick(doc)}
                          title="Certify document"
                        >
                          <CheckCircle fontSize="small" />
                        </IconButton>
                      )} */}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {documents.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <AdvancedPagination
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={e => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Rows per page:"
          />
        </Box>
      )}

      {/* View Document Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false)
          setSelectedDocument(null)
          setShowSignatureSection(false)
          setSignatureData(null)
          if (signaturePadRef.current) {
            signaturePadRef.current.clear()
          }
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Description color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600}>
              PoC Document Details
            </Typography>
          </Box>
          {selectedDocument && (
            <Chip
              label={capitalize(selectedDocument.poc_status)}
              color={getStatusColor(selectedDocument.poc_status) as any}
              icon={
                selectedDocument.poc_status === 'certified' ? (
                  <Verified fontSize="small" />
                ) : undefined
              }
              sx={{ fontWeight: 600 }}
            />
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: '2rem !important' }}>
          {selectedDocument && (
            <Box>
              {/* Document Information Cards */}
              <Stack spacing={2} sx={{ mb: 3 }}>
                <Card
                  variant="outlined"
                  sx={{
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <CardContent>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                          gutterBottom
                          display="block"
                        >
                          Contract ID
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={500}
                          sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                        >
                          {getContractId(selectedDocument.contract_id)}
                        </Typography>
                      </Box>
                      <Divider orientation="vertical" flexItem />
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                          gutterBottom
                          display="block"
                        >
                          Session ID
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={500}
                          sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                        >
                          {getSessionId(selectedDocument.session_id)}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Status Cards */}
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Card
                    variant="outlined"
                    sx={{
                      flex: 1,
                      minWidth: 200,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                        gutterBottom
                        display="block"
                      >
                        Status
                      </Typography>
                      <Chip
                        label={capitalize(selectedDocument.poc_status)}
                        color={
                          getStatusColor(selectedDocument.poc_status) as any
                        }
                        icon={
                          selectedDocument.poc_status === 'certified' ? (
                            <Verified fontSize="small" />
                          ) : undefined
                        }
                        sx={{ mt: 1, fontWeight: 600 }}
                      />
                    </CardContent>
                  </Card>
                  <Card
                    variant="outlined"
                    sx={{
                      flex: 1,
                      minWidth: 200,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                        gutterBottom
                        display="block"
                      >
                        Authorization Status
                      </Typography>
                      <Chip
                        label={capitalize(
                          selectedDocument.authorization_status
                        )}
                        color={
                          selectedDocument.authorization_status === 'approved'
                            ? 'success'
                            : selectedDocument.authorization_status === 'denied'
                              ? 'error'
                              : 'warning'
                        }
                        sx={{ mt: 1, fontWeight: 600 }}
                      />
                    </CardContent>
                  </Card>
                  <Card
                    variant="outlined"
                    sx={{
                      flex: 1,
                      minWidth: 200,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                        gutterBottom
                        display="block"
                      >
                        Created At
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight={500}>
                          {formatDate(selectedDocument.createdAt)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Stack>
              </Stack>

              {/* Document Content */}
              <Box>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                >
                  <Assignment color="primary" />
                  Document Content
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    maxHeight: '500px',
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      bgcolor: 'grey.200',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      bgcolor: 'grey.400',
                      borderRadius: '4px',
                      '&:hover': {
                        bgcolor: 'grey.500',
                      },
                    },
                  }}
                >
                  {selectedDocument.poc_doc ? (
                    <Box
                      sx={{
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '0.95rem',
                        lineHeight: 1.7,
                        color: 'text.primary',
                        '& h1, & h2, & h3, & h4': {
                          margin: '0 0 0.75rem',
                          fontWeight: 600,
                        },
                        '& h1': {
                          fontSize: '1.5rem',
                          marginBottom: '1rem',
                        },
                        '& h2': {
                          fontSize: '1.25rem',
                          marginTop: '1.5rem',
                          marginBottom: '0.75rem',
                        },
                        '& h3': {
                          fontSize: '1.1rem',
                          marginTop: '1.25rem',
                          marginBottom: '0.75rem',
                        },
                        '& p': {
                          margin: '0 0 0.75rem',
                        },
                        '& ul, & ol': {
                          paddingLeft: '1.5rem',
                          margin: '0 0 0.75rem',
                          listStyleType: 'disc',
                        },
                        '& ol': {
                          listStyleType: 'decimal',
                        },
                        '& li': {
                          marginBottom: '0.5rem',
                          display: 'list-item',
                          listStylePosition: 'outside',
                        },
                        '& ul li': {
                          listStyleType: 'disc',
                        },
                        '& ol li': {
                          listStyleType: 'decimal',
                        },
                        '& strong': {
                          fontWeight: 600,
                        },
                        '& em': {
                          fontStyle: 'italic',
                        },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: markdownToHtml(selectedDocument.poc_doc),
                      }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontStyle="italic"
                    >
                      No content available
                    </Typography>
                  )}
                </Paper>
              </Box>
            </Box>
          )}

          {/* Signature Capture Section - Only show for submitted status */}
          {selectedDocument && selectedDocument.poc_status === 'submitted' && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                >
                  <Edit color="primary" />
                  Capture Signature
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Please provide your signature to certify this PoC document.
                  Sign in the box below using your mouse or touch screen.
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    border: '2px dashed',
                    borderColor: signatureData ? 'success.main' : 'divider',
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                    bgcolor: 'white',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 180,
                    transition: 'border-color 0.3s ease',
                    '& canvas': {
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      cursor: 'crosshair',
                      maxWidth: '100%',
                      height: 'auto',
                    },
                  }}
                >
                  <SignatureCanvas
                    ref={signaturePadRef}
                    canvasProps={{
                      width: 500,
                      height: 150,
                      className: 'signature-canvas',
                    }}
                    onEnd={handleSignatureEnd}
                  />
                </Paper>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClearSignature}
                    disabled={!signatureData}
                  >
                    Clear Signature
                  </Button>
                  {signatureData && (
                    <Chip
                      icon={<Verified />}
                      label="Signature Captured"
                      color="success"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>
                {signatureData && (
                  <Box
                    sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Signature Preview
                    </Typography>
                    <Box
                      component="img"
                      src={signatureData}
                      alt="Signature"
                      sx={{
                        maxWidth: '100%',
                        maxHeight: 80,
                        objectFit: 'contain',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 0.5,
                        bgcolor: 'white',
                      }}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5, bgcolor: 'grey.50' }}>
          <>
            <Button
              onClick={handleCancelCertify}
              variant="outlined"
              disabled={certifying}
            >
              Cancel
            </Button>
            {selectedDocument?.poc_status === 'submitted' && (
              <Button
                variant="contained"
                color="success"
                onClick={handleCertifyWithSignature}
                disabled={certifying || !signatureData}
                startIcon={
                  certifying ? <CircularProgress size={16} /> : <CheckCircle />
                }
              >
                {certifying ? 'Certifying...' : 'Certify Document'}
              </Button>
            )}
          </>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PoCDocumentsPage
